import dateFormat from "dateformat";
import {
  getMeasurementSerial,
  normalizeSerial,
} from "./equipmentCompare";
import { buildDataQualityReport } from "./dataInsights";
import {
  collectMetadataSummary,
  collectUniqueProcesses,
  getMeasureProcessLabel,
} from "../../../utils/measureFields";

const COLORS = {
  primary: [28, 66, 32],
  accent: [143, 173, 21],
  text: [32, 42, 36],
  muted: [95, 110, 98],
  light: [245, 248, 242],
  white: [255, 255, 255],
  line: [210, 220, 208],
  warning: [196, 150, 30],
  danger: [180, 55, 55],
  cardBg: [236, 242, 230],
};

const PERIOD_LABELS = {
  0: "Último dia",
  1: "Últimos 7 dias",
  2: "Último mês",
  3: "Últimos 3 meses",
};

const parseNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const normalized = String(value).trim().replace(",", ".");
  if (!normalized) return null;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
};

const avg = (values) => {
  if (!values.length) return null;
  const sum = values.reduce((acc, value) => acc + value, 0);
  return sum / values.length;
};

const fmt = (value, digits = 1, suffix = "") => {
  if (value === null || value === undefined || Number.isNaN(value)) return "--";
  return `${Number(value).toFixed(digits)}${suffix}`;
};

const fmtDate = (value) =>
  value ? dateFormat(value, "UTC:dd/mm/yyyy' 'HH:MM") : "--";

const loadImageAsDataUrl = (src) =>
  new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth || image.width;
        canvas.height = image.naturalHeight || image.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(image, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = src;
  });

const buildFilterSummary = ({
  startDate,
  endDate,
  ultimoClicado,
  lastData,
  filterData,
  customFilter,
  hours,
  selectedUnitsCount,
  equipmentCount,
}) => {
  const lines = [];

  if (startDate && endDate) {
    lines.push(`Intervalo: ${startDate} até ${endDate}`);
  } else if (ultimoClicado !== null && PERIOD_LABELS[ultimoClicado]) {
    lines.push(`Período: ${PERIOD_LABELS[ultimoClicado]} (${lastData} dias)`);
  } else if (hours?.start) {
    lines.push(`Dados: ${hours.start} — ${hours.end || "--"}`);
  } else {
    lines.push("Período: padrão da consulta");
  }

  if (filterData === "input") lines.push("Faixa: Entrada (umidade > 14%)");
  else if (filterData === "output") lines.push("Faixa: Saída (umidade < 13,5%)");
  else lines.push("Faixa: Todas as medições");

  const advanced = Object.entries(customFilter || {})
    .filter(([, value]) => String(value || "").trim())
    .map(([key, value]) => `${key.toUpperCase()}: ${value}`);

  if (advanced.length) {
    lines.push(`Filtros avançados: ${advanced.join(" · ")}`);
  }

  lines.push(
    `Seleção: ${selectedUnitsCount} unidade(s) · ${equipmentCount} equipamento(s)`
  );

  return lines;
};

const buildCurveAverages = (dataList = []) => {
  const grouped = dataList.reduce((acc, item) => {
    const grain = String(item?.grain || "Sem curva").trim() || "Sem curva";
    if (!acc[grain]) acc[grain] = [];
    acc[grain].push(item);
    return acc;
  }, {});

  return Object.entries(grouped)
    .map(([grain, items]) => {
      const humidity = avg(
        items.map((item) => parseNumber(item.humidity)).filter((v) => v !== null)
      );
      const temperature = avg(
        items
          .map((item) => parseNumber(item.temperature))
          .filter((v) => v !== null)
      );
      const ph = avg(
        items.map((item) => parseNumber(item.ph)).filter((v) => v !== null)
      );

      return {
        grain,
        humidity,
        temperature,
        ph,
        total: items.length,
      };
    })
    .sort((a, b) => b.total - a.total);
};

const minOf = (values) => (values.length ? Math.min(...values) : null);
const maxOf = (values) => (values.length ? Math.max(...values) : null);

const collectUnique = (items, getter) => {
  const values = new Set();
  items.forEach((item) => {
    const value = String(getter(item) || "").trim();
    if (value) values.add(value);
  });
  return [...values];
};

const buildMetricStats = (items, getter) => {
  const values = items
    .map((item) => parseNumber(getter(item)))
    .filter((value) => value !== null);
  return {
    avg: avg(values),
    min: minOf(values),
    max: maxOf(values),
    count: values.length,
  };
};

const buildEquipmentSummary = (dataList = [], equipmentOptions = []) => {
  const bySerial = new Map();

  dataList.forEach((item) => {
    const serial =
      normalizeSerial(getMeasurementSerial(item)) ||
      String(item?.nameEquipament || "SEM SERIAL").toUpperCase();
    if (!bySerial.has(serial)) {
      bySerial.set(serial, []);
    }
    bySerial.get(serial).push(item);
  });

  const optionMap = new Map(
    (equipmentOptions || []).map((option) => [
      normalizeSerial(option.serial),
      option,
    ])
  );

  return [...bySerial.entries()]
    .map(([serial, items]) => {
      const option = optionMap.get(serial);
      const sorted = [...items].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      const humidity = buildMetricStats(items, (item) => item.humidity);
      const temperature = buildMetricStats(items, (item) => item.temperature);
      const ph = buildMetricStats(items, (item) => item.ph);
      const peso = buildMetricStats(items, (item) =>
        item?.peso ? String(item.peso).replace(",", ".") : null
      );
      const curves = collectUnique(items, (item) => item.grain);
      const locais = collectUnique(items, (item) => item.localName);
      const contratos = collectUnique(items, (item) => item.contrato);
      const lotes = collectUnique(items, (item) => item.lote);
      const placas = collectUnique(items, (item) => item.placa);
      const cnpjs = collectUnique(items, (item) => item.cnpj);
      const processes = collectUniqueProcesses(items);
      const metadataSummary = collectMetadataSummary(items);

      return {
        serial: option?.serial || serial,
        label: option?.label || items[0]?.nameEquipament || serial,
        unitName: option?.unitName || "",
        model: option?.modelLabel || option?.model || "",
        total: items.length,
        humidity,
        temperature,
        ph,
        peso,
        curves,
        locais,
        contratos,
        lotes,
        placas,
        cnpjs,
        processes,
        metadataSummary,
        lastProcess: getMeasureProcessLabel(sorted[sorted.length - 1]),
        firstDate: sorted[0]?.date,
        lastDate: sorted[sorted.length - 1]?.date,
      };
    })
    .sort((a, b) => b.total - a.total);
};

const buildOverallStats = (dataList = []) => {
  const humidityValues = dataList
    .map((item) => parseNumber(item.humidity))
    .filter((v) => v !== null);
  const temperatureValues = dataList
    .map((item) => parseNumber(item.temperature))
    .filter((v) => v !== null);
  const phValues = dataList
    .map((item) => parseNumber(item.ph))
    .filter((v) => v !== null);

  const sorted = [...dataList].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  return {
    total: dataList.length,
    humidityAvg: avg(humidityValues),
    humidityMin: humidityValues.length ? Math.min(...humidityValues) : null,
    humidityMax: humidityValues.length ? Math.max(...humidityValues) : null,
    temperatureAvg: avg(temperatureValues),
    temperatureMin: temperatureValues.length
      ? Math.min(...temperatureValues)
      : null,
    temperatureMax: temperatureValues.length
      ? Math.max(...temperatureValues)
      : null,
    phAvg: avg(phValues),
    firstDate: sorted[0]?.date,
    lastDate: sorted[sorted.length - 1]?.date,
    curves: new Set(
      dataList.map((item) => String(item?.grain || "").trim()).filter(Boolean)
    ).size,
  };
};

/**
 * Gera PDF detalhado dos dados filtrados da tela de Relatórios.
 */
export const generateRelatoriosPdf = async ({
  user,
  allData = [],
  equipmentOptions = [],
  selectedUnitsCount = 0,
  startDate = "",
  endDate = "",
  ultimoClicado = null,
  lastData = 29,
  filterData = "all",
  customFilter = {},
  hours = {},
  curvaSelecionada = "",
} = {}) => {
  const { default: jsPDF } = await import("jspdf");
  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 14;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const logoWhite = await loadImageAsDataUrl(
    "/images/motomco_logo_reduzida_branca.png"
  );
  const logoDark = await loadImageAsDataUrl("/images/logo.png");

  const stats = buildOverallStats(allData);
  const quality = buildDataQualityReport({ allData, equipmentOptions });
  const curves = buildCurveAverages(allData);
  const equipments = buildEquipmentSummary(allData, equipmentOptions);
  const filterLines = buildFilterSummary({
    startDate,
    endDate,
    ultimoClicado,
    lastData,
    filterData,
    customFilter,
    hours,
    selectedUnitsCount,
    equipmentCount: equipmentOptions.length,
  });
  const processStages = collectUniqueProcesses(allData);
  if (processStages.length) {
    filterLines.push(`Etapas do processo nos dados: ${processStages.join(" · ")}`);
  }

  const now = new Date();
  const generatedAt = dateFormat(now, "dd/mm/yyyy HH:MM");
  const fileDate = dateFormat(now, "yyyy-mm-dd_HH-MM");

  const ensureSpace = (needed) => {
    if (y + needed <= pageHeight - 18) return;
    pdf.addPage();
    drawPageHeader();
    y = 28;
  };

  const drawFooter = () => {
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i += 1) {
      pdf.setPage(i);
      pdf.setFillColor(...COLORS.primary);
      pdf.rect(0, pageHeight - 12, pageWidth, 12, "F");
      pdf.setTextColor(...COLORS.white);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.text("SGU · Relatório de Medições", margin, pageHeight - 4.5);
      pdf.text(
        `Página ${i} de ${pageCount}`,
        pageWidth / 2,
        pageHeight - 4.5,
        { align: "center" }
      );
      pdf.text(generatedAt, pageWidth - margin, pageHeight - 4.5, {
        align: "right",
      });
    }
  };

  const drawPageHeader = () => {
    pdf.setFillColor(...COLORS.primary);
    pdf.rect(0, 0, pageWidth, 18, "F");
    pdf.setFillColor(...COLORS.accent);
    pdf.rect(0, 18, pageWidth, 1.2, "F");
    pdf.setTextColor(...COLORS.white);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.text("Relatório de Medições · SGU", margin, 11.5);
    if (logoWhite) {
      pdf.addImage(logoWhite, "PNG", pageWidth - margin - 38, 3.5, 36, 11);
    }
  };

  const sectionTitle = (title) => {
    ensureSpace(16);
    pdf.setFillColor(...COLORS.light);
    pdf.roundedRect(margin, y, contentWidth, 9, 2, 2, "F");
    pdf.setFillColor(...COLORS.accent);
    pdf.roundedRect(margin, y, 2.2, 9, 1, 1, "F");
    pdf.setTextColor(...COLORS.primary);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.text(title, margin + 6, y + 6);
    y += 14;
  };

  const drawKpiCard = (x, title, value, subtitle) => {
    const width = (contentWidth - 9) / 4;
    pdf.setFillColor(...COLORS.cardBg);
    pdf.roundedRect(x, y, width, 22, 2.5, 2.5, "F");
    pdf.setDrawColor(...COLORS.line);
    pdf.setLineWidth(0.2);
    pdf.roundedRect(x, y, width, 22, 2.5, 2.5, "S");

    pdf.setTextColor(...COLORS.muted);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.text(title, x + 3, y + 6);

    pdf.setTextColor(...COLORS.primary);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.text(String(value), x + 3, y + 13.5);

    pdf.setTextColor(...COLORS.muted);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.text(subtitle || "", x + 3, y + 18.5);
  };

  const drawTable = (headers, rows, colWidths) => {
    const rowHeight = 7;
    const headerHeight = 8;

    const drawHeader = () => {
      ensureSpace(headerHeight + rowHeight);
      let x = margin;
      pdf.setFillColor(...COLORS.primary);
      pdf.roundedRect(margin, y, contentWidth, headerHeight, 1.5, 1.5, "F");
      pdf.setTextColor(...COLORS.white);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      headers.forEach((header, index) => {
        pdf.text(header, x + 2, y + 5.2);
        x += colWidths[index];
      });
      y += headerHeight;
    };

    drawHeader();

    rows.forEach((row, rowIndex) => {
      if (y + rowHeight > pageHeight - 18) {
        pdf.addPage();
        drawPageHeader();
        y = 28;
        drawHeader();
      }

      if (rowIndex % 2 === 0) {
        pdf.setFillColor(...COLORS.light);
        pdf.rect(margin, y, contentWidth, rowHeight, "F");
      }

      let x = margin;
      pdf.setTextColor(...COLORS.text);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7.5);
      row.forEach((cell, index) => {
        const text = String(cell ?? "--");
        const maxWidth = Math.max(8, colWidths[index] - 3);
        const clipped = pdf.splitTextToSize(text, maxWidth)[0] || text;
        pdf.text(clipped, x + 2, y + 4.8);
        x += colWidths[index];
      });
      y += rowHeight;
    });
  };

  // ===== CAPA =====
  pdf.setFillColor(...COLORS.primary);
  pdf.rect(0, 0, pageWidth, pageHeight, "F");
  pdf.setFillColor(...COLORS.accent);
  pdf.rect(0, 0, 8, pageHeight, "F");

  if (logoWhite) {
    pdf.addImage(logoWhite, "PNG", margin + 8, 42, 78, 24);
  } else if (logoDark) {
    pdf.addImage(logoDark, "PNG", margin + 8, 42, 55, 22);
  }

  pdf.setTextColor(...COLORS.white);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(28);
  pdf.text("Relatório de", margin + 8, 95);
  pdf.text("Medições", margin + 8, 108);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(12);
  pdf.setTextColor(...COLORS.accent);
  pdf.text("SGU · Análise consolidada dos dados filtrados", margin + 8, 120);

  pdf.setDrawColor(...COLORS.accent);
  pdf.setLineWidth(0.8);
  pdf.line(margin + 8, 126, margin + 70, 126);

  pdf.setTextColor(220, 230, 220);
  pdf.setFontSize(10);
  const coverMeta = [
    `Gerado em ${generatedAt}`,
    `Usuário: ${user?.hideSensitiveInfo ? "*****" : user?.name || user?.email || "--"}`,
    `Registros: ${stats.total}`,
    `Curva em foco: ${curvaSelecionada || "Todas"}`,
  ];
  coverMeta.forEach((line, index) => {
    pdf.text(line, margin + 8, 145 + index * 8);
  });

  pdf.setFillColor(...COLORS.accent);
  pdf.roundedRect(margin + 8, pageHeight - 42, 62, 10, 2, 2, "F");
  pdf.setTextColor(...COLORS.primary);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.text("CONFIDENCIAL", margin + 18, pageHeight - 35.5);

  // ===== RESUMO =====
  pdf.addPage();
  drawPageHeader();
  y = 28;

  sectionTitle("1. Resumo executivo");
  ensureSpace(28);
  drawKpiCard(margin, "MEDIÇÕES", String(stats.total), `${stats.curves} curva(s)`);
  drawKpiCard(
    margin + (contentWidth - 9) / 4 + 3,
    "UMIDADE MÉDIA",
    fmt(stats.humidityAvg, 1, "%"),
    `${fmt(stats.humidityMin)} – ${fmt(stats.humidityMax)}`
  );
  drawKpiCard(
    margin + 2 * ((contentWidth - 9) / 4 + 3),
    "TEMP. MÉDIA",
    fmt(stats.temperatureAvg, 1, "°C"),
    `${fmt(stats.temperatureMin)} – ${fmt(stats.temperatureMax)}`
  );
  drawKpiCard(
    margin + 3 * ((contentWidth - 9) / 4 + 3),
    "pH / DENSIDADE",
    fmt(stats.phAvg, 1),
    "média do período"
  );
  y += 28;

  sectionTitle("2. Filtros aplicados");
  ensureSpace(10 + filterLines.length * 6);
  pdf.setFillColor(...COLORS.light);
  pdf.roundedRect(margin, y, contentWidth, 8 + filterLines.length * 6, 2, 2, "F");
  pdf.setTextColor(...COLORS.text);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  filterLines.forEach((line, index) => {
    pdf.text(`•  ${line}`, margin + 4, y + 7 + index * 6);
  });
  y += 14 + filterLines.length * 6;

  sectionTitle("3. Qualidade dos dados");
  ensureSpace(30);
  const qualityCards = [
    ["Cobertura", `${quality.coveragePercent}%`],
    ["Com medição", `${quality.withDataCount}/${quality.selectedCount || 0}`],
    ["Gaps", String(quality.gapCount)],
    ["Intervalo típico", quality.medianIntervalMinutes != null ? `${quality.medianIntervalMinutes} min` : "--"],
  ];
  qualityCards.forEach(([label, value], index) => {
    const width = (contentWidth - 9) / 4;
    const x = margin + index * (width + 3);
    pdf.setFillColor(...COLORS.white);
    pdf.setDrawColor(...COLORS.line);
    pdf.roundedRect(x, y, width, 16, 2, 2, "FD");
    pdf.setTextColor(...COLORS.muted);
    pdf.setFontSize(7);
    pdf.text(label, x + 3, y + 5.5);
    pdf.setTextColor(...COLORS.primary);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.text(value, x + 3, y + 12);
  });
  y += 22;

  pdf.setTextColor(...COLORS.muted);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8.5);
  pdf.text(
    `Período nos dados: ${fmtDate(stats.firstDate)} — ${fmtDate(stats.lastDate)}`,
    margin,
    y
  );
  y += 10;

  // ===== CURVAS =====
  sectionTitle("4. Médias por curva");
  if (!curves.length) {
    pdf.setTextColor(...COLORS.muted);
    pdf.text("Nenhuma curva disponível nos dados filtrados.", margin, y);
    y += 10;
  } else {
    drawTable(
      ["Curva", "Umidade", "Temperatura", "pH/Dens.", "Medições"],
      curves.map((item) => [
        item.grain,
        fmt(item.humidity, 1, "%"),
        fmt(item.temperature, 1, "°C"),
        fmt(item.ph, 1),
        String(item.total),
      ]),
      [55, 30, 35, 30, 32]
    );
    y += 8;
  }

  // ===== EQUIPAMENTOS =====
  sectionTitle("5. Resumo por equipamento");
  if (!equipments.length) {
    pdf.setTextColor(...COLORS.muted);
    pdf.text("Nenhum equipamento identificado nos dados.", margin, y);
    y += 10;
  } else {
    const equipmentRows = equipments.slice(0, 40).map((item) => [
      item.label,
      item.unitName || "--",
      fmt(item.humidity.avg, 1, "%"),
      fmt(item.temperature.avg, 1, "°C"),
      String(item.total),
    ]);
    drawTable(
      ["Equipamento", "Unidade", "Umid.", "Temp.", "Qtd"],
      equipmentRows,
      [55, 50, 25, 25, 27]
    );
    if (equipments.length > 40) {
      y += 4;
      pdf.setTextColor(...COLORS.muted);
      pdf.setFontSize(8);
      pdf.text(
        `Exibindo 40 de ${equipments.length} equipamentos no resumo.`,
        margin,
        y
      );
      y += 8;
    } else {
      y += 8;
    }
  }

  // ===== DETALHES POR EQUIPAMENTO =====
  sectionTitle("6. Detalhes por equipamento");
  pdf.setTextColor(...COLORS.muted);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.text(
    "Para cada equipamento: média, mínima e máxima das medições, além de período, curvas e metadados disponíveis.",
    margin,
    y
  );
  y += 8;

  if (!equipments.length) {
    pdf.setTextColor(...COLORS.muted);
    pdf.text("Não há equipamentos para detalhar com os filtros atuais.", margin, y);
  } else {
    const joinLimited = (values, limit = 4) => {
      if (!values?.length) return "--";
      if (values.length <= limit) return values.join(", ");
      return `${values.slice(0, limit).join(", ")} +${values.length - limit}`;
    };

    const metricLine = (label, metric, suffix = "") =>
      `${label}: méd ${fmt(metric.avg, 1, suffix)}  ·  mín ${fmt(metric.min, 1, suffix)}  ·  máx ${fmt(metric.max, 1, suffix)}`;

    equipments.forEach((item, index) => {
      const extraLines = [];
      if (item.model) extraLines.push(`Modelo: ${item.model}`);
      if (item.locais.length) extraLines.push(`Local: ${joinLimited(item.locais)}`);
      if (item.curves.length) extraLines.push(`Curvas: ${joinLimited(item.curves, 6)}`);
      if (item.contratos.length) extraLines.push(`Contrato: ${joinLimited(item.contratos)}`);
      if (item.lotes.length) extraLines.push(`Lote: ${joinLimited(item.lotes)}`);
      if (item.placas.length) extraLines.push(`Placa: ${joinLimited(item.placas)}`);
      if (item.cnpjs.length) extraLines.push(`CNPJ: ${joinLimited(item.cnpjs)}`);
      if (item.processes?.length) {
        extraLines.push(`Processo: ${joinLimited(item.processes, 6)}`);
      }
      if (item.lastProcess && item.lastProcess !== "--") {
        extraLines.push(`Última etapa: ${item.lastProcess}`);
      }
      item.metadataSummary?.forEach((meta) => {
        if (meta.values.length) {
          extraLines.push(`${meta.label}: ${joinLimited(meta.values, 3)}`);
        }
      });
      if (item.peso.count) {
        extraLines.push(
          `Peso: méd ${fmt(item.peso.avg, 2, " g")} · mín ${fmt(item.peso.min, 2, " g")} · máx ${fmt(item.peso.max, 2, " g")}`
        );
      }

      const cardHeight = 34 + Math.max(0, Math.ceil(extraLines.length / 2) * 5);
      ensureSpace(cardHeight + 4);

      pdf.setFillColor(...COLORS.light);
      pdf.roundedRect(margin, y, contentWidth, cardHeight, 2.5, 2.5, "F");
      pdf.setDrawColor(...COLORS.line);
      pdf.setLineWidth(0.25);
      pdf.roundedRect(margin, y, contentWidth, cardHeight, 2.5, 2.5, "S");
      pdf.setFillColor(...COLORS.accent);
      pdf.roundedRect(margin, y, 2.2, cardHeight, 1, 1, "F");

      pdf.setTextColor(...COLORS.primary);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9.5);
      const title = `${index + 1}. ${item.label}`;
      pdf.text(pdf.splitTextToSize(title, contentWidth - 55)[0], margin + 5, y + 6);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7.5);
      pdf.setTextColor(...COLORS.muted);
      pdf.text(`Serial: ${item.serial}`, pageWidth - margin - 3, y + 6, {
        align: "right",
      });

      pdf.setTextColor(...COLORS.text);
      pdf.setFontSize(8);
      pdf.text(
        `Unidade: ${item.unitName || "--"}  ·  Medições: ${item.total}  ·  Período: ${fmtDate(item.firstDate)} — ${fmtDate(item.lastDate)}`,
        margin + 5,
        y + 12
      );

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(...COLORS.primary);
      pdf.text(metricLine("Umidade", item.humidity, "%"), margin + 5, y + 18);
      pdf.text(metricLine("Temperatura", item.temperature, "°C"), margin + 5, y + 23.5);
      pdf.text(metricLine("pH/Densidade", item.ph), margin + 5, y + 29);

      if (extraLines.length) {
        pdf.setTextColor(...COLORS.muted);
        pdf.setFontSize(7.5);
        extraLines.forEach((line, lineIndex) => {
          const col = lineIndex % 2;
          const row = Math.floor(lineIndex / 2);
          const x = margin + 5 + col * (contentWidth / 2);
          pdf.text(
            pdf.splitTextToSize(line, contentWidth / 2 - 8)[0],
            x,
            y + 35 + row * 5
          );
        });
      }

      y += cardHeight + 3.5;
    });
  }

  // ===== ENCERRAMENTO =====
  ensureSpace(36);
  y += 8;
  pdf.setFillColor(...COLORS.primary);
  pdf.roundedRect(margin, y, contentWidth, 28, 3, 3, "F");
  pdf.setTextColor(...COLORS.white);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text("Observações", margin + 5, y + 8);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8.5);
  pdf.text(
    "Este relatório foi gerado automaticamente a partir dos filtros aplicados na tela de Relatórios.",
    margin + 5,
    y + 15
  );
  pdf.text(
    "Os detalhes por equipamento mostram média, mínima e máxima calculadas sobre as medições filtradas.",
    margin + 5,
    y + 21
  );

  drawFooter();

  const safeName = (user?.hideSensitiveInfo ? "SGU" : "Relatorio_Unidades")
    .replace(/[^\w-]+/g, "_");
  const fileName = `${safeName}_${fileDate}.pdf`;
  pdf.save(fileName);

  return { fileName, total: stats.total };
};
